import { useEffect, useRef, useCallback } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

// Reusable guided tour built on driver.js (vanilla, React 19 safe).
// - auto: when true, auto-starts once per browser (localStorage) — used for the
//   public Landing visitor tour.
// - Replay/on-demand: dispatch window event `lm-run-tour` with { key } matching
//   storageKey to start it any time, regardless of the "seen" flag.
// Anchors must be unique #tour-* ids; steps to hidden/zero-box nodes are skipped
// so the spotlight always lands on the correct, visible element.
export function GuidedTour({ steps, storageKey, auto = true }) {
  const startedOnce = useRef(false);

  const start = useCallback(() => {
    const isVisible = (el) => !!el && el.getClientRects().length > 0;
    const present = steps.filter((s) => isVisible(document.querySelector(s.target)));
    if (!present.length) return;
    const persist = () => localStorage.setItem(storageKey, "1");
    const d = driver({
      showProgress: true,
      allowClose: true,
      overlayColor: "#0a0a14",
      overlayOpacity: 0.55,
      stagePadding: 6,
      popoverClass: "lm-tour",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      steps: present.map((s) => ({
        element: s.target,
        popover: { title: s.title, description: s.content },
      })),
      onDestroyed: persist,
      onCloseClick: () => { persist(); d.destroy(); },
    });
    d.drive();
  }, [steps, storageKey]);

  // Auto-once (Landing visitor tour).
  useEffect(() => {
    if (!auto || startedOnce.current || typeof window === "undefined") return;
    if (localStorage.getItem(storageKey)) return;
    startedOnce.current = true;
    const t = setTimeout(start, 900);
    return () => clearTimeout(t);
  }, [auto, storageKey, start]);

  // On-demand start (welcome nudge for new accounts + "Take the tour" replay).
  useEffect(() => {
    const handler = (e) => { if (e.detail?.key === storageKey) start(); };
    window.addEventListener("lm-run-tour", handler);
    return () => window.removeEventListener("lm-run-tour", handler);
  }, [storageKey, start]);

  return null;
}

// Helper for buttons elsewhere in the app to trigger a tour by its storageKey.
export function runTour(storageKey) {
  window.dispatchEvent(new CustomEvent("lm-run-tour", { detail: { key: storageKey } }));
}

export default GuidedTour;
